/**
 * Submission list item component.
 *
 * @module SubmissionListItem
 */

import { Button } from '@/components/ui/button';
import { Hash, Loader2, Mail, Trash2, User } from 'lucide-react';
import { SimulationResultCard } from '@/components/results/SimulationResultCard';
import { UserSubmission } from './types';
import { buildSubmissionSimulationResult } from './submissionResultAdapter';

interface SubmissionListItemProps {
  readonly submission: UserSubmission;
  readonly onPreviewSubmission: (submission: UserSubmission, module: string) => void;
  readonly onDelete: (submission: UserSubmission) => void;
  readonly isDeleting: boolean;
  readonly loadingReviewKey: string | null;
}

export function SubmissionListItem({
  submission,
  onPreviewSubmission,
  onDelete,
  isDeleting,
  loadingReviewKey,
}: SubmissionListItemProps) {
  const result = buildSubmissionSimulationResult(submission);
  const isLoadingThisSubmission =
    loadingReviewKey?.startsWith(`${submission.session_id}:`) ?? false;

  return (
    <SimulationResultCard
      result={result}
      variant="compact"
      moduleActionLabel="Preview"
      headerContent={(
        <div className="flex flex-col gap-1.5 text-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 space-y-1">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="font-ui flex min-w-0 items-center gap-1 font-semibold text-foreground">
                <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{submission.username}</span>
              </span>
              <span className="font-ui flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
                <Mail className="h-3 w-3 shrink-0" />
                <span className="truncate">{submission.email}</span>
              </span>
            </div>
            <div className="font-ui flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground">
              <Hash className="h-3 w-3 shrink-0" />
              <span className="truncate">Session: {submission.session_id}</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 self-end sm:self-center">
            {isLoadingThisSubmission ? (
              <span className="font-ui flex items-center gap-1 text-[11px] text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading preview
              </span>
            ) : null}
            <Button
              size="sm"
              variant="destructive"
              onClick={() => onDelete(submission)}
              disabled={isDeleting || loadingReviewKey !== null}
              className="h-7 px-2 text-xs"
            >
              {isDeleting ? (
                <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
              ) : (
                <Trash2 className="h-3 w-3 mr-1.5" />
              )}
              <span className="font-ui">Delete</span>
            </Button>
          </div>
        </div>
      )}
      onModuleClick={(module) => {
        if (loadingReviewKey !== null) return;
        onPreviewSubmission(submission, module);
      }}
    />
  );
}
